import {Breadcrumb, BreadcrumbHint} from "@sentry/browser";
import {IPaymentTransaction} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import {ICartMessage} from "@connox/typescript-library/types/cart/CartMessage";
import * as Sentry from "@sentry/browser";
import {ApiError, Checkout, Customer, IGetCheckoutShippingOptionsData} from "@connox/typescript-library";
import {IBraintreeError} from "../components/checkout/steps/Summary/Summary";
import {CheckoutStep} from "../components/checkout/Checkout";

type TransactionCategoryType = "createPayment" | "initPayment" | "authorizePayment" | "initBraintreeClient";

/**
 * breadcrumb preparation and addition of information
 * used on sentry init in 'index.tsx'
 * @param breadcrumb
 * @param hint
 */
export const beforeBreadcrumb = (
  breadcrumb: Breadcrumb,
  hint?: {
    xhr?: {
      response: {success: boolean; result: IPaymentTransaction; messages: ICartMessage[]} | string;
      status: number;
    };
  } & BreadcrumbHint,
): Breadcrumb => {
  // if url contains "auth" substring -> replace it
  if (breadcrumb.data?.url) {
    const url = breadcrumb.data.url as string;
    breadcrumb.data.url = authReplacer(url);
  }

  // if xhr.status explicitly exists and is of 0/400/500 type
  if (hint?.xhr?.status) {
    const {status} = hint.xhr;
    if (status < 200 || 399 < status) {
      Sentry.addBreadcrumb({
        level: "debug",
        category: "faulty.xhr.status",
        data: {
          response: authReplacer(
            typeof hint?.xhr?.response === "string"
              ? hint?.xhr?.response
              : JSON.stringify(hint?.xhr?.response, null, 2),
          ),
          status,
        },
        type: "error",
      });
    }
  }

  return breadcrumb;
};

/**
 * capture exception in sentry with additional information
 * @param err
 * @param checkout
 * @param occurence
 * @param additionalInfo
 */
export const captureSentryException = (
  err: ApiError | IBraintreeError | Error,
  checkout: Checkout,
  occurence: string,
  additionalInfo = {},
): string => {
  additionalInfo = {...additionalInfo, logisticsType: checkout.logisticsTypeId, paymentType: checkout.paymentId};

  // delete default toJSON method to get plain object
  delete (err as {toJSON?: () => {[key: string]: unknown}}).toJSON;
  // stringify error by using replacer method
  const errString = authReplacer(JSON.stringify(err, replacer, 2));

  return Sentry.captureException(err, {
    extra: {
      ...getAllMessages(checkout),
      additionalInfo,
      occurence,
      errorDetails: errString,
      checkoutPaymentTransaction: authReplacer(JSON.stringify(checkout.paymentTransaction, null, 2)),
    },
  });
};

/**
 * default JSON.stringify replacer method.
 * deletes/replaces some security relevant fields, that would otherwise lead to filtering in sentry
 * @param key
 * @param value just any json valid data. type safety here only for linter satisfactory
 */
const replacer = (key, value: string | undefined | {withCredentials?: boolean; withCredenteals?: boolean}) => {
  // just replace dom elements with simple string, to avoid circular structures
  if (value instanceof HTMLElement) {
    return "[HTMLElement]";
  }

  // replace bearer token with respective string
  if (typeof value === "string" && value.startsWith("Bearer ")) return "[BearerToken]";

  // undefineds will be deleted, so for informational reasons keep them with string 'undefined'
  if (value === undefined) return "undefined";

  // return value as is
  return value;
};

/**
 * extract all message nodes from object
 * @param obj
 */
const getAllMessages = (obj: Record<string, any>): {[key: string]: ICartMessage[]} => {
  let msgObject: Record<string, ICartMessage[]> = {};
  try {
    Object.entries<{messages: ICartMessage[]; text: string}>(obj).forEach(([entryKey, entryValue]) => {
      if (entryValue?.messages?.length) {
        /* if key has only decimals (array index, as in cart.positions[])
         * and position.text is set, take position.text as string key
         */
        const objKey = "MSGS_" + (!/\D/.test(entryKey) && entryValue.text ? entryValue.text : entryKey);
        msgObject[objKey] = entryValue.messages.map((msg: ICartMessage) => ({
          field: msg.field,
          type: msg.type,
          code: authReplacer(msg.code),
          message: authReplacer(msg.message),
        }));
      }

      if (
        entryKey !== "messages" &&
        typeof entryValue === "object" &&
        entryValue !== null &&
        !(entryValue instanceof HTMLElement)
      ) {
        msgObject = {...msgObject, ...getAllMessages(entryValue)};
      }
    });
  } catch (e) {
    return {
      ...msgObject,
      internalError: [{code: "internal", type: "error", field: "unknown", message: (e as Error).message}],
    };
  }

  return msgObject;
};

/**
 * general add breadcrumb function including some meta information
 * @param step
 * @param checkout
 * @param reason
 * @param shippings
 * @param customer
 */
export const sentryAddBreadcrumb = (
  step: CheckoutStep | "opening cart" | "closing cart",
  checkout: Checkout | null,
  reason: string,
  shippings: IGetCheckoutShippingOptionsData[] | undefined,
  customer: Customer,
): void =>
  Sentry.addBreadcrumb({
    category: "step.change",
    level: "info",
    message: step,
    type: "user",
    data: {
      customerStatus: customer.isLoggedIn ? "logged in" : customer.isRecognised ? "recognized" : "new",
      checkoutData: checkout ? {
        ...checkout.paymentTransaction,
        logisticsType: checkout.logisticsTypeId,
        paymentType: checkout.paymentId,
      } : undefined,
      reason,
      shippings: shippings ? shippings.map((so) => so.logisticsTypeId) : [],
    },
  });

/**
 * breadcrumb function particularly for the payment transactions
 * @param transactionCategory
 * @param checkout checkout object **_before_** the transactions are called
 * @param additionalData
 */
export const sentryAddPaymentBreadcrumb = <T>(
  transactionCategory: TransactionCategoryType,
  checkout: Checkout,
  additionalData: T,
): void => {
  const additional: T & {[key: string]: ICartMessage | IPaymentTransaction | null} = {
    ...additionalData,
    ...getAllMessages(checkout),
    checkoutPaymentTransaction: checkout.paymentTransaction,
  };
  Sentry.addBreadcrumb({
    category: transactionCategory,
    level: "info",
    type: "transaction",
    data: additional,
  });
};

const authReplacer = (input: string): string => {
  return input
    .replace(/auth/gi, "auTT")
    .replace(/token/gi, "T0KEN")
    .replace(/withCredentials/g, "withCredenteals");
};
